import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatDate';

import type { TenantEmployees } from '../api/tenant-users';
import type { EmployeeRoleType } from '../schema/employee-role';
import { AddEmployeeButton } from './add-employee-button';
import { DeleteEmployeeButton } from './delete-employee-button';
import { EditEmployeeButton } from './edit-employee-button';

interface TenantEmployeeListProps {
  employees: TenantEmployees;
  tenantId: string;
  currentUserRole?: EmployeeRoleType['role'];
  isSuperAdmin: boolean;
}

export const TenantEmployeeList = ({
  employees,
  tenantId,
  currentUserRole,
  isSuperAdmin,
}: TenantEmployeeListProps) => {
  const { t } = useTranslation();
  const canEditEmployeeRole =
    isSuperAdmin || currentUserRole === 'admin' || currentUserRole === 'supervisor';
  const canDeleteEmployee = isSuperAdmin || currentUserRole === 'admin';
  const canManageEmployees = canEditEmployeeRole || canDeleteEmployee;

  return (
    <div className="size-full">
      <Card className="px-4 py-6 lg:px-10">
        <div className="flex justify-between">
          <div>
            <h2 className="text-md text-primary-dark">{t('team')}</h2>
          </div>
          <AddEmployeeButton tenantId={tenantId} />
        </div>
        {employees.length ? (
          <Table className="my-5 w-full table-auto bg-transparent">
            <TableHeader className="h-11 max-sm:hidden">
              <TableRow className="w-full justify-between">
                <TableHead className="text-center text-text-secondary">{t('name')}</TableHead>
                <TableHead className="text-center text-text-secondary">{t('email')}</TableHead>
                <TableHead className="text-center text-text-secondary max-md:hidden">
                  {t('created-at')}
                </TableHead>
                <TableHead className="text-center text-text-secondary">{t('role')}</TableHead>
                {canManageEmployees ? (
                  <TableHead className="text-center text-text-secondary">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => (
                <TableRow
                  key={employee.user.id}
                  className="cursor-pointer items-center text-primary-dark"
                >
                  <TableCell className="w-1/4 max-w-40 whitespace-normal break-words py-5 text-center">
                    {`${employee.user.firstName} ${employee.user.lastName}`}
                  </TableCell>
                  <TableCell className="w-1/4 max-w-40 whitespace-normal break-words py-5 text-center">
                    {employee.user.email}
                  </TableCell>
                  <TableCell className="w-1/4 max-w-40 whitespace-normal break-words py-5 text-center max-md:hidden">
                    {formatDate(employee.createdAt)}
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {t(employee.role.replaceAll(' ', '-'))}
                  </TableCell>
                  {canManageEmployees ? (
                    <TableCell className="flex max-w-40 gap-2 lg:w-1/4">
                      {canEditEmployeeRole ? (
                        <EditEmployeeButton
                          id={employee.user.id}
                          role={employee.role as EmployeeRoleType['role']}
                          tenantId={tenantId}
                        />
                      ) : null}
                      {canDeleteEmployee ? (
                        <DeleteEmployeeButton id={employee.user.id} tenantId={tenantId} />
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex justify-center py-16">
            <span>{t('no-team')}</span>
          </div>
        )}
      </Card>
    </div>
  );
};
